// Stage-04 "ניתוח מצב קיים" (existing-condition analysis) data layer — chunk 04.
//
// The stage models the existing building as SIX independent inference routes
// (מסלולי הסקה), each of which observes some of the same data fields from a
// different kind of evidence. The user then picks, per FIELD, which route's
// observation wins (ריכוז נתונים), and the chosen values assemble into a
// schematic of the building (סכמת מצב קיים).
//
// Nothing here computes rights or a proposed state — those are later stages.
//
// Data sources: the chunk-02 identification snapshot (parcelFacts, builtAs,
// buildingFile) plus documents the user attaches to a route. Routes whose
// evidence is inherently a user document (manual, photos, survey, tabu) cannot
// self-run — they report their missing documents instead of inventing values.

import apiConfig from '../../../config/api';
import { DATABASES } from './statutory';

const BASE = apiConfig.apiBaseUrl || '';

// Building footprints on the parcel, measured from the municipal buildings
// layer. Sent the PARCEL POLYGON rather than a point — a geocoded point lands
// in the neighbour's building often enough to be unusable. Returns null on any
// failure, which the route reports as "could not answer" rather than a zero.
export async function fetchBuildingFootprint(parcel, signal) {
  const rings = parcel?.geometry?.rings || null;
  const coords = parcel?.coords || null;
  if (!rings && !coords) return null;
  try {
    const res = await fetch(`${BASE}/api/ask?action=building-footprint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rings, x_itm: coords?.x, y_itm: coords?.y }),
      signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.footprint || null;
  } catch { return null; }
}

// Bump when the route/field construction changes so stale snapshots re-derive.
// v2 — routes read the chunk-03 reviewed layer, and the GIS route no longer
// claims the building file's coverage as its own observation.
// v3 — the GIS route measures the footprint off the municipal buildings layer.
export const EX_VERSION = 'v3';

// Reuse is keyed by the PROPERTY, exactly as chunks 02/03 do: drop the version
// prefix so a version bump never discards a completed stage's user edits.
export const exPropKey = (sig) => String(sig || '').split('|').slice(1).join('|');

// ─── Route statuses (§5) ────────────────────────────────────────────────────
export const ROUTE_STATUS = {
  not_run: { label: 'לא הופעל', tone: 'neutral', action: 'הפעלה' },
  ready: { label: 'מוכן להפעלה', tone: 'info', action: 'הפעלה' },
  missing_docs: { label: 'חסרים מסמכים', tone: 'warn', action: 'השלמת מסמכים' },
  completed: { label: 'הופעל בהצלחה', tone: 'ok', action: 'הרצה מחדש' },
  partial: { label: 'הופעל חלקית', tone: 'warn', action: 'הרצה מחדש' },
  needs_rerun: { label: 'דורש הרצה מחדש', tone: 'warn', action: 'הרצה מחדש' },
  failed: { label: 'נכשל בהרצה', tone: 'bad', action: 'הרצה מחדש' },
};

// ─── Value statuses (§12) ───────────────────────────────────────────────────
// Deliberately the SAME KEYS as the v4 §14 field statuses used by chunk 03
// (statutory.js FIELD_STATUS) so one vocabulary flows through the whole app —
// only the labels are the chunk-04 wording. `conflict` is the one genuinely new
// state: two routes observed the same field and disagree.
// Labels match chunk 03's FIELD_STATUS verbatim, so a datum doesn't rename its
// own status when the user walks one stage forward — and so no bare Latin word
// sits inside an RTL Hebrew pill.
export const VALUE_STATUS = {
  approved: { label: 'אושר', tone: 'ok' },
  check: { label: 'ממתין לאישור', tone: 'warn' },
  override: { label: 'שונה ידנית', tone: 'info' },
  user_input: { label: 'הזנה ידנית', tone: 'info' },
  not_in_scope: { label: 'לא נדרש', tone: 'neutral' },
  missing: { label: 'חסר', tone: 'bad' },
  not_found: { label: 'חסר במסלול הנבחר', tone: 'warn' },
  conflict: { label: 'סתירה בין מסלולים', tone: 'bad' },
};

export const CONFIDENCE_LABEL = {
  high: { label: 'גבוהה', tone: 'ok' },
  medium: { label: 'בינונית', tone: 'warn' },
  low: { label: 'נמוכה', tone: 'bad' },
  user_defined: { label: 'משתמש', tone: 'info' },
  missing: { label: '—', tone: 'neutral' },
};

// Two numeric observations count as a conflict only past this relative gap —
// OCR rounding and a surveyor's decimals shouldn't read as a contradiction.
// (The spec asks for a conflict state but doesn't set a tolerance; this is the
// pilot default and the one number to revisit with the client.)
export const CONFLICT_TOLERANCE_PCT = 2;

// ─── The six inference routes (§7) ──────────────────────────────────────────
// `kind: 'auto'` — the agent can run it from data the system already holds.
// `kind: 'document'` — the evidence IS a user document; the route can only run
// once one is attached, and it never invents a value from a filename.
export const ROUTES = [
  {
    id: 'manual_input',
    num: 1,
    name: 'הזנה ידנית / קובץ משתמש',
    kind: 'document',
    database: null,
    explain: 'הזנה ישירה של נתוני המבנה על ידי המשתמש, או קובץ נתונים (טבלת שטחים, Excel) שהוא מעלה. זהו User Input — לא מקור רשמי, אלא אם צורף מסמך תומך.',
    inputs: 'טופס ידני, קובץ Excel, טבלת שטחים של יזם, PDF עם נתונים כתובים.',
    limits: 'זהו User Input. הוא לא מקור אוטומטי ולא מקור רשמי, אלא אם צורף מסמך תומך.',
    confidence: 'user_defined',
  },
  {
    id: 'image_analysis',
    num: 2,
    name: 'תמונות / תיעוד חזותי',
    kind: 'document',
    database: DATABASES.visual,
    explain: 'ניתוח חזיתות, חלונות ומרפסות מתוך תמונות המשתמש, Street View או תמונות אלכסוניות, כדי להעריך קומות, מרפסות ומספר יחידות.',
    inputs: 'תמונות משתמש, חזיתות, תמונות צד, תמונות גג, תמונות אלכסוניות מתוך GIS / Google Street View.',
    limits: 'בדרך כלל אינו יודע להפיק שטח יחידה מדויק, שטח קומה מדויק או חניה בטאבו. התוצאות מסומנות כהסקה חזותית — אינדיקציה בלבד.',
    confidence: 'low',
  },
  {
    id: 'gis_layers',
    num: 3,
    name: 'GIS / שכבות עירוניות',
    kind: 'auto',
    database: DATABASES.gis,
    explain: 'מאתר את המבנה בתוך החלקה, מחשב תכסית מתוך פוליגון המבנה, ומזהה מבנים נוספים אם קיימים.',
    inputs: 'שכבת מבנים, פוליגון מבנה, שכבת קומות, שכבת שימושים, מודל תלת-ממדי.',
    limits: 'לא יודע להפיק חלוקה פנימית לדירות, שטח כל יחידה, חניה בטאבו או מרפסות סגורות באופן אמין.',
    confidence: 'medium',
  },
  {
    id: 'measurement_plan',
    num: 4,
    name: 'מדידה / תכנית מצב קיים',
    kind: 'document',
    database: DATABASES.survey,
    explain: 'קורא את התכנית כבסיס הפיזי של הבניין — קומות, יחידות, גבולות דירות, שטחי יחידות, מרפסות, חדרי מדרגות וגג.',
    inputs: 'מדידת פנים, מדידה אדריכלית, תכנית מצב קיים, PDF מדידה, DWG, קובץ מודד.',
    limits: 'מדידה היא מקור פיזי. היא לא מוכיחה שהשטחים אושרו בהיתר או שהחלוקה תואמת את המצב הרישויי.',
    confidence: 'high',
  },
  {
    id: 'condominium_registry',
    num: 5,
    name: 'נסח טאבו / בית משותף',
    kind: 'document',
    database: DATABASES.tabu,
    explain: 'מנתח את המצב הקנייני: סופר תתי חלקות, מזהה יחידות רשומות, ומבחין בין דירות, חניות מוצמדות, מחסנים וגגות מוצמדים.',
    inputs: 'נסח טאבו מרוכז, נסח לפי תת חלקה, צו בית משותף, תשריט בית משותף, תקנון.',
    limits: 'זהו מקור קנייני. הוא לא מוכיח שהדירה קיימת בפועל או שהיא מאושרת בהיתר. מספר תתי חלקות אינו בהכרח מספר יחידות דיור מאושר.',
    confidence: 'high',
  },
  {
    id: 'building_permits',
    num: 6,
    name: 'תיק בניין / היתרים / תשריטים',
    kind: 'auto',
    database: DATABASES.buildingFile,
    explain: 'מזהה סוגי מסמכים בתיק, מחלץ נתונים ישירות מעמוד ראשון של ההיתר ומטבלאות השטחים, וקורא תכניות קומות וחזיתות.',
    inputs: 'היתר מקורי, היתרי שינוי, גרמושקה, עמוד ראשון של ההיתר, טבלת שטחים, תכנית קומת קרקע, קומה טיפוסית, גג.',
    limits: 'המסלול אמין מבחינה רישויית, אבל לעיתים קשה להרצה בגלל מסמכים חסרים, סריקות באיכות נמוכה, תכניות ללא קנה מידה, או סתירה בין היתר מקורי להיתר שינוי.',
    confidence: 'high',
  },
];

export const routeById = (id) => ROUTES.find((r) => r.id === id) || null;

// ─── The field catalogue (§9) ───────────────────────────────────────────────
// The focused list needed to build an existing-condition schematic — NOT a full
// building survey. `routes` is which routes can plausibly observe the field
// (§7 "תוצר אפשרי"); `required` gates the §13 completion check.
//
// NOTE: the spec requires "every mandatory datum has a selected value" but never
// says which fields are mandatory. These five are the ones the schematic and the
// downstream rights calc cannot be built without — confirm the real list with
// the client and adjust `required` here.
export const FIELD_GROUPS = [
  {
    id: 'structure',
    title: 'קומות ומבנה כללי',
    fields: [
      { id: 'above_ground_floors_count', name: 'מספר קומות מעל הקרקע', unit: 'קומות', type: 'number', required: true, routes: ['building_permits', 'measurement_plan', 'gis_layers', 'image_analysis', 'manual_input'] },
      { id: 'below_ground_floors_count', name: 'מספר קומות מתחת לקרקע', unit: 'קומות', type: 'number', routes: ['building_permits', 'measurement_plan', 'manual_input'] },
      { id: 'ground_floor_type', name: 'סוג קומת קרקע', unit: '', type: 'text', required: true, routes: ['building_permits', 'measurement_plan', 'image_analysis', 'manual_input'] },
      { id: 'building_height_m', name: 'גובה המבנה', unit: 'מ׳', type: 'number', routes: ['building_permits', 'measurement_plan', 'gis_layers', 'manual_input'] },
    ],
  },
  {
    id: 'units',
    title: 'יחידות דיור',
    fields: [
      { id: 'existing_units_count', name: 'מספר יחידות דיור קיימות', unit: 'יח״ד', type: 'number', required: true, routes: ['condominium_registry', 'building_permits', 'measurement_plan', 'image_analysis', 'manual_input'] },
      { id: 'typical_floor_units_count', name: 'מספר יחידות דיור בקומה טיפוסית', unit: 'יח״ד', type: 'number', routes: ['measurement_plan', 'building_permits', 'image_analysis', 'manual_input'] },
      { id: 'unit_area_avg_sqm', name: 'שטח ממוצע ליחידה', unit: 'מ״ר', type: 'number', routes: ['measurement_plan', 'condominium_registry', 'building_permits', 'manual_input'] },
    ],
  },
  {
    id: 'areas',
    title: 'שטחים לפי קומות',
    fields: [
      { id: 'typical_floor_area_sqm', name: 'שטח קומה טיפוסית', unit: 'מ״ר', type: 'number', required: true, routes: ['measurement_plan', 'building_permits', 'gis_layers', 'manual_input'] },
      { id: 'ground_floor_area_sqm', name: 'שטח קומת קרקע', unit: 'מ״ר', type: 'number', routes: ['measurement_plan', 'building_permits', 'gis_layers', 'manual_input'] },
      { id: 'building_footprint_sqm', name: 'תכסית המבנה', unit: 'מ״ר', type: 'number', routes: ['gis_layers', 'measurement_plan', 'building_permits', 'manual_input'] },
      { id: 'total_built_area_sqm', name: 'סה״כ שטח בנוי', unit: 'מ״ר', type: 'number', required: true, routes: ['building_permits', 'measurement_plan', 'manual_input'] },
      { id: 'main_area_sqm', name: 'שטח עיקרי', unit: 'מ״ר', type: 'number', routes: ['building_permits', 'measurement_plan', 'manual_input'] },
      { id: 'service_area_sqm', name: 'שטח שירות', unit: 'מ״ר', type: 'number', routes: ['building_permits', 'measurement_plan', 'manual_input'] },
      { id: 'roof_built_area_sqm', name: 'שטח בנוי על הגג', unit: 'מ״ר', type: 'number', routes: ['measurement_plan', 'building_permits', 'image_analysis', 'manual_input'] },
    ],
  },
  {
    id: 'balconies',
    title: 'מרפסות',
    fields: [
      { id: 'balconies_exist', name: 'קיום מרפסות', unit: '', type: 'bool', routes: ['image_analysis', 'measurement_plan', 'building_permits', 'manual_input'] },
      { id: 'closed_balconies_exist', name: 'קיום מרפסות סגורות', unit: '', type: 'bool', routes: ['image_analysis', 'measurement_plan', 'manual_input'] },
      { id: 'balcony_area_sqm', name: 'שטח מרפסות', unit: 'מ״ר', type: 'number', routes: ['measurement_plan', 'building_permits', 'manual_input'] },
    ],
  },
  {
    id: 'parking',
    title: 'חניה',
    fields: [
      { id: 'parking_exists', name: 'קיום חניה', unit: '', type: 'bool', routes: ['gis_layers', 'image_analysis', 'measurement_plan', 'condominium_registry', 'manual_input'] },
      { id: 'parking_count', name: 'מספר חניות', unit: 'חניות', type: 'number', routes: ['condominium_registry', 'measurement_plan', 'building_permits', 'manual_input'] },
      { id: 'ground_parking_area_sqm', name: 'שטח חניה קרקעית', unit: 'מ״ר', type: 'number', routes: ['measurement_plan', 'gis_layers', 'manual_input'] },
      { id: 'attached_parking', name: 'חניות מוצמדות בטאבו', unit: '', type: 'text', routes: ['condominium_registry', 'manual_input'] },
    ],
  },
  {
    id: 'roof',
    title: 'גג',
    fields: [
      { id: 'roof_status', name: 'גג פנוי / בנוי', unit: '', type: 'text', routes: ['image_analysis', 'measurement_plan', 'building_permits', 'manual_input'] },
      { id: 'roof_exit_rooms', name: 'חדרי יציאה לגג', unit: '', type: 'text', routes: ['measurement_plan', 'building_permits', 'manual_input'] },
      { id: 'attached_roofs', name: 'גגות מוצמדים בטאבו', unit: '', type: 'text', routes: ['condominium_registry', 'manual_input'] },
    ],
  },
];

export const ALL_FIELDS = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => ({ ...f, groupId: g.id, groupTitle: g.title })));
export const fieldDef = (id) => ALL_FIELDS.find((f) => f.id === id) || null;

// Which route wins when several observed the same field and the user hasn't
// chosen. The spec shows a selected value per row but never states the rule;
// this is the domain default — legal facts from the registry, licensing facts
// from the permit, physical facts from the survey.
const GLOBAL_PRECEDENCE = ['building_permits', 'measurement_plan', 'condominium_registry', 'gis_layers', 'image_analysis', 'manual_input'];
const FIELD_PRECEDENCE = {
  existing_units_count: ['condominium_registry', 'building_permits', 'measurement_plan', 'image_analysis', 'manual_input'],
  parking_count: ['condominium_registry', 'measurement_plan', 'building_permits', 'manual_input'],
  attached_parking: ['condominium_registry', 'manual_input'],
  attached_roofs: ['condominium_registry', 'manual_input'],
  typical_floor_area_sqm: ['measurement_plan', 'building_permits', 'gis_layers', 'manual_input'],
  ground_floor_area_sqm: ['measurement_plan', 'building_permits', 'gis_layers', 'manual_input'],
  unit_area_avg_sqm: ['measurement_plan', 'condominium_registry', 'building_permits', 'manual_input'],
  building_footprint_sqm: ['gis_layers', 'measurement_plan', 'building_permits', 'manual_input'],
  balcony_area_sqm: ['measurement_plan', 'building_permits', 'manual_input'],
};
const precedenceFor = (fieldId) => FIELD_PRECEDENCE[fieldId] || GLOBAL_PRECEDENCE;

// ─── Observations ───────────────────────────────────────────────────────────
const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

function observation(routeId, fieldId, value, opts = {}) {
  const def = fieldDef(fieldId);
  return {
    routeId,
    fieldId,
    fieldName: fieldId,
    displayName: def?.name || fieldId,
    value: value === null || value === undefined ? null : value,
    unit: def?.unit || '',
    source: opts.source || null, // { label, link, database, dataset }
    confidence: opts.confidence || 'medium',
    // 'observed' — the route produced a value. 'not_found' — the route ran but
    // its evidence doesn't carry this field (the spec's "חסר במסלול הנבחר").
    status: value === null || value === undefined ? 'not_found' : (opts.status || 'observed'),
    note: opts.note || '',
  };
}

// A value the user already reviewed in stage 03, looked up by its stable
// fieldName key. Stage 03 is the reviewed layer over the same municipal
// sources, so anything it holds outranks re-deriving from the raw snapshot —
// otherwise a correction made there is silently thrown away here.
// Only a field the user actually DECIDED counts as reviewed. Stage 03 builds
// its fields from raw OCR with status 'check' (ממתין לאישור) by default, so
// accepting any non-empty value made us stamp "אושר בשלב הסטטוטורי" on numbers
// nobody had looked at — the exact false provenance this stage exists to
// prevent. An undecided field falls through to the raw sources instead, where
// it gets labelled for what it is.
const REVIEWED_STATUSES = new Set(['approved', 'override', 'user_input']);

export function reviewedField(statutory, fieldName) {
  for (const g of statutory?.groups || []) {
    for (const f of g.fields || []) {
      if (f.fieldName !== fieldName) continue;
      if (f.value === null || f.value === undefined || String(f.value).trim() === '') continue;
      if (!REVIEWED_STATUSES.has(f.status)) continue;
      return f;
    }
  }
  return null;
}

// Stage 03 stores every value as a STRING, so a perfectly good reviewed field
// can be "כ-11" or "1,200". Parse leniently; a value we cannot read must fall
// back to the raw source rather than erase it.
const numFromReviewed = (f) => {
  if (!f) return null;
  // Unsigned: counts and areas are never negative, and a leading hyphen here is
  // far more likely to be the Hebrew "כ-" ("approximately") than a minus sign.
  const raw = String(f.value).replace(/,/g, '').match(/\d+(\.\d+)?/);
  return raw ? Number(raw[0]) : null;
};

// Route 3 — GIS / municipal layers. Measures the footprint off the buildings
// layer (ctx.gis, from fetchBuildingFootprint). The area is a real geometric
// measurement, so it carries high confidence; the layer's own storey and height
// attributes are noisy and are only offered when the server's plausibility
// check passed, always at low confidence.
function runGisRoute(ctx) {
  const gis = ctx?.gis || null;
  const src = {
    label: 'שכבת מבני העיר (GIS)',
    link: null,
    database: DATABASES.gis,
    dataset: { id: 'ds_gis_buildings', name: 'שכבת מבנים — פוליגון מבנה' },
  };
  const ALL = ['building_footprint_sqm', 'ground_floor_area_sqm', 'above_ground_floors_count', 'building_height_m', 'ground_floor_type', 'typical_floor_area_sqm', 'parking_exists'];
  if (!gis) {
    const note = 'לא אותר מבנה בשכבת המבנים עבור החלקה.';
    return ALL.map((id) => observation('gis_layers', id, null, { source: src, note }));
  }

  // The server falls back to a 12 m circle around the geocoded point when the
  // parcel has no polygon. That is the neighbour's-building trap, so it must not
  // wear a high-confidence "measured against the parcel boundary" label.
  const approximated = gis.matched_by !== 'parcel_polygon';
  const areaConfidence = approximated ? 'low' : 'high';
  const boundary = approximated
    ? 'לא אותר פוליגון חלקה — הבדיקה בוצעה ברדיוס 12 מ׳ סביב נקודת הכתובת, וייתכן שנכללו מבני שכנים. טעון אימות.'
    : 'התכסית היא סכום שטחי החפיפה עם גבול החלקה.';
  const multi = gis.buildings_count > 1
    ? `נמצאו ${gis.buildings_count} מבנים. ${boundary}`
    : boundary;
  const obs = [
    observation('gis_layers', 'building_footprint_sqm', gis.footprint_m2, { source: src, confidence: areaConfidence, note: multi }),
    // The footprint is the ground floor's outline, not its measured built area.
    observation('gis_layers', 'ground_floor_area_sqm', gis.footprint_m2, {
      source: src, confidence: 'low', note: 'מוסק מתכסית המבנה — אינו מדידת שטח בנוי בפועל.',
    }),
    // The layer counts TOTAL storeys; this field means storeys ABOVE the ground
    // floor (see buildSchematic). They are different quantities, and feeding one
    // into the other produced a permanent unresolvable conflict against the
    // permit — and, if resolved toward GIS, a building one storey too tall.
    // There is no reliable conversion, so the route reports what it has and
    // declines to answer this field.
    observation('gis_layers', 'above_ground_floors_count', null, {
      source: src,
      note: gis.stories == null
        ? 'מספר הקומות בשכבה אינו עקבי מול גובה המבנה ולכן לא הוצג.'
        : `בשכבה רשומות ${gis.stories} קומות בסך הכול — מדד שונה ממספר הקומות מעל הקרקע, ולכן אינו משמש כאן.`,
    }),
    observation('gis_layers', 'building_height_m', gis.height_m ?? null, {
      source: src, confidence: 'low',
      note: gis.height_m == null
        ? 'גובה המבנה בשכבה חורג מטווח סביר ולכן לא הוצג.'
        : 'מחושב ממודל פני שטח (הפרש בין הגובה המרבי למזערי בתחום המבנה) — אינו מדידה אדריכלית וטעון אימות מול ההיתר.',
    }),
  ];
  // t_amudim ("עמודים") in the layer marks a building on pilotis — a direct
  // answer to a required field we were leaving blank.
  obs.push(observation('gis_layers', 'ground_floor_type', gis.pilotis === true ? 'עמודים מפולשת' : null, {
    source: src,
    confidence: 'low',
    note: gis.pilotis === true
      ? 'מאפיין "עמודים" בשכבת המבנים — טעון אימות מול ההיתר.'
      : 'שכבת המבנים אינה מציינת עמודים מפולשים למבנה זה.',
  }));
  for (const id of ['typical_floor_area_sqm', 'parking_exists']) {
    obs.push(observation('gis_layers', id, null, { source: src, note: 'שכבת המבנים אינה מכילה נתון זה.' }));
  }
  return obs;
}

// Route 6 — building file / permits. Consumes the permit record + the OCR'd
// building-file facts, but prefers whatever stage 03 already reviewed.
function runPermitsRoute(ctx) {
  const idn = ctx?.identification || null;
  const statutory = ctx?.statutory || null;
  const built = idn?.builtAs?.built_as || null;
  const pf = idn?.parcelFacts || null;
  // TWO different evidence trails, and they must not be mixed. `evidence_url`
  // is the citation from the as-built / טופס 4 lookup; `parcelFacts.source_urls`
  // are the OCR'd pages the area and coverage were read from. Linking every
  // observation to the former meant the footprint pointed at a page that had
  // nothing to do with where its number came from.
  const builtEvidence = idn?.builtAs?.evidence_url || null;
  const factsEvidence = pf?.source_urls?.[0] || null;
  const obs = [];
  // Values that come from the structured permit record.
  const src = {
    label: built?.permit_number ? `היתר ${built.permit_number}` : 'תיק בניין עירוני',
    link: builtEvidence,
    database: DATABASES.buildingFile,
    dataset: { id: 'ds_permit_first_page', name: built ? 'עמוד ראשון של ההיתר / טופס 4' : 'תיק בניין' },
  };
  // Values read out of the scanned pages by OCR + extraction.
  const srcFacts = {
    label: 'תיק בניין (OCR)',
    link: factsEvidence,
    database: DATABASES.buildingFile,
    dataset: { id: 'ds_building_file_ocr', name: 'עמודים סרוקים מתיק הבניין' },
  };
  const push = (fieldId, value, confidence = 'high', note = '', source = src) => obs.push(
    observation('building_permits', fieldId, value, { source, confidence, note })
  );
  // A reviewed stage-03 field carries its own source and the user's decision;
  // reuse both, and describe the decision accurately — approving a value and
  // typing one over it are not the same act.
  const WORDING = { approved: 'אושר בשלב הסטטוטורי', override: 'שונה ידנית בשלב הסטטוטורי', user_input: 'הוזן ידנית בשלב הסטטוטורי' };
  const fromStage3 = (f) => ({
    label: f.source?.label ? `${f.source.label} · ${WORDING[f.status]}` : `שלב 03 — ${WORDING[f.status]}`,
    link: f.source?.link || factsEvidence,
    database: f.database || DATABASES.buildingFile,
    dataset: f.dataset || src.dataset,
  });

  push('above_ground_floors_count', num(built?.stories_above_ground));
  push('below_ground_floors_count', num(built?.stories_below_ground));
  push('total_built_area_sqm', num(built?.built_area_m2));
  push('building_height_m', num(built?.building_height_m));

  // Units: a DECIDED stage-03 value first, then the permit record, then the
  // OCR'd file (lowest certainty — it comes from free text). A reviewed value we
  // cannot parse must not erase the fallbacks.
  const rvUnits = reviewedField(statutory, 'units_existing');
  const rvUnitsNum = numFromReviewed(rvUnits);
  if (rvUnitsNum !== null) {
    push('existing_units_count', rvUnitsNum, rvUnits.confidence === 'missing' ? 'medium' : rvUnits.confidence,
      `הערך מ${WORDING[rvUnits.status]}`, fromStage3(rvUnits));
  } else if (num(built?.units_count) !== null) {
    push('existing_units_count', num(built.units_count));
  } else {
    push('existing_units_count', num(pf?.units_count), 'medium',
      pf?.units_count != null ? 'מתוך OCR של תיק הבניין — לא מרשומת היתר מובנית' : '', srcFacts);
  }

  // Footprint. Prefer the figure the permit STATES ("בונה — 156.99 מ״ר") over
  // recomputing area × coverage: the coverage percentage is what the plan
  // ALLOWED, and multiplying it out produces a permitted figure wearing the
  // clothes of a measurement. Either way the year is carried, because these are
  // historical numbers — a 1966 form does not describe the building today.
  const rvCoverage = reviewedField(statutory, 'coverage_actual');
  const rvLotArea = reviewedField(statutory, 'lot_area');
  const stated = num(pf?.built_footprint_m2);
  const year = num(pf?.facts_year);
  const rawDate = pf?.facts_date_raw ? String(pf.facts_date_raw).trim() : null;
  // Show the date as it was READ, not only as it was interpreted: these are
  // handwritten scans, and a misread digit shifts the permit by years.
  const asOf = year
    ? ` (לפי הטופס משנת ${year}${rawDate ? `, תאריך שנקרא בסריקה: ${rawDate} — יש לאמת מול המסמך` : ''})`
    : ' (נתון היסטורי מתיק הבניין)';
  if (stated !== null) {
    push('building_footprint_sqm', stated, 'medium', `שטח בנוי כפי שנרשם בטופס ההיתר${asOf}. אינו מדידה של המצב היום.`, srcFacts);
  } else {
    const coverage = numFromReviewed(rvCoverage) ?? num(pf?.coverage_pct);
    const lotArea = numFromReviewed(rvLotArea) ?? num(pf?.parcel_area_m2);
    const footprint = coverage !== null && lotArea !== null ? Math.round((lotArea * coverage) / 100) : null;
    push('building_footprint_sqm', footprint, 'medium',
      footprint === null ? '' : `מחושב משטח מגרש ${lotArea} מ״ר × תכסית ${coverage}%${asOf} — אחוז מותר, לא מדידה של הבנוי בפועל.`,
      (numFromReviewed(rvCoverage) !== null) ? fromStage3(rvCoverage) : srcFacts);
  }

  for (const id of ['typical_floor_area_sqm', 'ground_floor_type', 'main_area_sqm', 'service_area_sqm', 'balcony_area_sqm', 'parking_count']) {
    push(id, null);
  }
  return obs;
}

// Document routes (1, 2, 4, 5) can't self-run. Running one with documents
// attached opens a slot per field it could produce, each marked "not found in
// the selected route" until the user reads the value out of the document — the
// same rule chunk 03 uses: linking a document never invents a value.
function runDocumentRoute(route, docs) {
  const doc = docs[0] || null;
  const src = {
    label: doc?.name || route.name,
    link: doc?.link || null,
    database: route.database || DATABASES.archive,
    dataset: { id: `ds_${route.id}`, name: doc?.name || 'מסמך שהועלה', type: 'uploaded_document' },
  };
  return ALL_FIELDS
    .filter((f) => (f.routes || []).includes(route.id))
    .map((f) => observation(route.id, f.id, null, { source: src, confidence: route.confidence }));
}

// ─── Route runs ─────────────────────────────────────────────────────────────
function routeState(route, docs, ctx) {
  if (route.kind === 'auto') {
    const idn = ctx?.identification || null;
    const hasData = route.id === 'building_permits'
      ? Boolean(idn?.builtAs?.built_as || idn?.parcelFacts || reviewedField(ctx?.statutory, 'units_existing'))
      // Route 3 needs a located parcel to measure against.
      : Boolean(idn?.parcel);
    return hasData ? 'ready' : 'missing_docs';
  }
  return docs.length ? 'ready' : 'missing_docs';
}

// Execute one route. `ctx` is { identification, statutory } — the chunk-02
// snapshot plus the chunk-03 reviewed layer.
export function executeRoute(route, ctx) {
  const docs = route.documents || [];
  // A document-evidence route with nothing attached has nothing to run against.
  // Leave it unrun (§5: opening it shows the gaps and the upload area) rather
  // than recording an empty run that would then offer a "re-run".
  if (route.kind === 'document' && !docs.length) {
    return { ...route, status: 'missing_docs', run: null };
  }
  let observations = [];
  if (route.id === 'gis_layers') observations = runGisRoute(ctx);
  else if (route.id === 'building_permits') observations = runPermitsRoute(ctx);
  else if (docs.length) observations = runDocumentRoute(route, docs);

  const found = observations.filter((o) => o.value !== null).length;
  let status = 'failed';
  if (!docs.length && route.kind === 'document') status = 'missing_docs';
  else if (!observations.length) status = 'failed';
  else if (found === 0) status = 'partial';
  else if (found < observations.length) status = 'partial';
  else status = 'completed';

  return {
    ...route,
    status,
    run: {
      runId: `${route.id}-${Date.now()}`,
      status,
      usedDocuments: docs.map((d) => d.name),
      observations,
      lastRunAt: new Date().toISOString(),
    },
  };
}

// ─── Consolidation (§8) ─────────────────────────────────────────────────────
const near = (a, b) => {
  const x = num(a); const y = num(b);
  if (x === null || y === null) return String(a) === String(b);
  const scale = Math.max(Math.abs(x), Math.abs(y)) || 1;
  return (Math.abs(x - y) / scale) * 100 <= CONFLICT_TOLERANCE_PCT;
};

// Rebuild one consolidation row from all routes' current observations, keeping
// whatever the user already decided about it.
export function consolidateField(def, routes, prior) {
  const all = [];
  for (const r of routes) {
    for (const o of (r.run?.observations || [])) {
      if (o.fieldId === def.id) all.push(o);
    }
  }
  const withValue = all.filter((o) => o.value !== null);

  // A resolution answers a specific set of competing values. Computed BEFORE the
  // override early-return, so a held-open override doesn't freeze it.
  const altSig = withValue.map((o) => `${o.routeId}=${o.value}`).sort().join('|');

  // A user decision always wins over a re-derivation.
  if (prior && (prior.status === 'override' || prior.status === 'user_input')) {
    // altSig must stay live even while the user's own value holds the row —
    // freezing it made the FIRST resolution afterwards resolve against a stale
    // candidate set, drop the pin, and re-raise the conflict.
    return { ...prior, alternatives: all, conflict: false, altSig };
  }

  const order = precedenceFor(def.id);
  const ranked = [...withValue].sort((a, b) => order.indexOf(a.routeId) - order.indexOf(b.routeId));
  // Honour an explicit route choice THE USER made, as long as that route still
  // has a value; otherwise fall back to precedence (§8: switching routes re-pulls
  // the value, and a route with no value leaves the row "חסר במסלול הנבחר").
  //
  // Only a user pin counts. `selectedRouteId` is also written by this function
  // when precedence picks, and treating that as a choice made the first route to
  // run permanently sticky: run the permit route before the GIS one and the
  // permit kept the row even though GIS outranks it for this field.
  // A pin is absolute: if the user chose a route that turns out to have no
  // value, the row reports "חסר במסלול הנבחר" and stays there. Falling back to
  // another route silently moved the row off the user's choice while keeping
  // their pin, which then re-created the sticky-route bug on the next run.
  const userPinned = Boolean(prior?.userResolution) && Boolean(prior?.selectedRouteId);
  const picked = userPinned
    ? (ranked.find((o) => o.routeId === prior.selectedRouteId) || null)
    : (ranked[0] || null);

  // Pairwise, not against withValue[0]. The old star comparison used whichever
  // route came first in ROUTES — manual entry — so typing a value between two
  // disagreeing official sources made their disagreement vanish from the gate.
  let conflict = false;
  for (let i = 0; i < withValue.length && !conflict; i++) {
    for (let j = i + 1; j < withValue.length; j++) {
      if (!near(withValue[i].value, withValue[j].value)) { conflict = true; break; }
    }
  }
  // When the candidate set changes — a route re-runs with a new number — the old
  // decision no longer covers it, so the conflict must resurface.
  const resolved = Boolean(prior?.userResolution) && prior.userResolution.altSig === altSig;

  let status;
  // "חסר במסלול הנבחר" only makes sense once a route IS selected and turns out
  // not to carry the field. With no route chosen the row is simply missing.
  if (!picked) status = (userPinned || prior?.selectedRouteId) ? 'not_found' : 'missing';
  else if (conflict && !resolved) status = 'conflict';
  // Approval is of a route's evidence, not of a number. If the row falls back to
  // a different route that happens to hold the same value, the approval does not
  // transfer with it.
  else if (prior?.status === 'approved' && prior.selectedValue === picked.value
    && prior.selectedRouteId === picked.routeId) status = 'approved';
  else status = 'check';

  return {
    fieldId: def.id,
    fieldName: def.id,
    displayName: def.name,
    unit: def.unit,
    type: def.type,
    groupId: def.groupId,
    required: Boolean(def.required),
    selectedValue: picked ? picked.value : null,
    selectedRouteId: picked ? picked.routeId : (userPinned ? prior.selectedRouteId : null),
    selectedSource: picked ? picked.source : null,
    confidence: picked ? picked.confidence : 'missing',
    status,
    conflict,
    alternatives: all,
    userResolution: resolved ? prior.userResolution : null,
    altSig,
    note: prior?.note || '',
    history: prior?.history || [],
    lastUpdated: prior?.lastUpdated || null,
  };
}

export function consolidate(routes, priorFields = []) {
  const priorById = new Map(priorFields.map((f) => [f.fieldId, f]));
  return ALL_FIELDS.map((def) => consolidateField(def, routes, priorById.get(def.id)));
}

// ─── The schematic (§10) ────────────────────────────────────────────────────
// Not a data table — a schematic of the building where every element that makes
// it up appears as its own rubric. Assembled from the SELECTED values only, so
// it always reflects what the user actually approved.
const val = (fields, id) => {
  const f = fields.find((x) => x.fieldId === id);
  return f && f.selectedValue !== null ? f.selectedValue : null;
};

// Build Spec Elements (§4/§14): the units, balconies and bands that make up the
// building, as ADDRESSABLE objects rather than throwaway render output. They
// have to persist because a unit's facade is something the user assigns — there
// is no route that can tell us which way a flat looks, so the assignment is data
// in its own right and must survive a re-derivation.
//
// `prior` carries user-set properties across a rebuild, matched on a stable id.
export function buildElements(fields, prior = []) {
  const priorById = new Map((prior || []).map((e) => [e.id, e]));
  const schematic = buildSchematic(fields);
  const out = [];
  for (const floor of schematic.floors) {
    let n = 0;
    for (const el of floor.elements) {
      n += 1;
      const id = `${el.type}-${floor.id}-${n}`;
      const kept = priorById.get(id);
      out.push({
        id,
        type: el.type,
        name: el.name,
        floorId: floor.id,
        floorName: floor.name,
        level: floor.level,
        area: el.area ?? null,
        sourceField: el.sourceField || null,
        derived: Boolean(el.derived),
        // User-assigned, never inferred.
        orientation: kept?.orientation ?? null,
        note: kept?.note || '',
      });
    }
  }
  return out;
}

// How much of the building has been placed on a facade — drives the §13 message
// and tells the user whether the matrix is meaningful yet.
export function orientationProgress(elements) {
  const placeable = (elements || []).filter(isFacadePlaceable);
  return {
    total: placeable.length,
    placed: placeable.filter((e) => e.orientation).length,
  };
}

export function buildSchematic(fields) {
  const above = num(val(fields, 'above_ground_floors_count'));
  const below = num(val(fields, 'below_ground_floors_count'));
  const units = num(val(fields, 'existing_units_count'));
  const perFloor = num(val(fields, 'typical_floor_units_count'));
  const typicalArea = num(val(fields, 'typical_floor_area_sqm'));
  const groundArea = num(val(fields, 'ground_floor_area_sqm'));
  const roofArea = num(val(fields, 'roof_built_area_sqm'));
  const balconyArea = num(val(fields, 'balcony_area_sqm'));
  const groundType = val(fields, 'ground_floor_type');
  const parkingArea = num(val(fields, 'ground_parking_area_sqm'));
  const unitArea = num(val(fields, 'unit_area_avg_sqm'));

  // A floor count must be a positive whole number within a sane range. Zero used
  // to satisfy the completion gate with an empty stack, a negative drew nothing,
  // and a typo of 40000 built 40,003 rows and hung the renderer.
  const MAX_FLOORS = 60;
  if (above === null || !Number.isFinite(above) || above < 1 || above > MAX_FLOORS) {
    return { floors: [], totals: null, ready: false, invalidFloors: above !== null };
  }

  // "מספר קומות מעל הקרקע" counts the numbered floors ABOVE the ground floor —
  // ground is level 0 and the roof is its own unnumbered band on top, matching
  // the chunk-04 visual's floor column (4·3·2·1·קרקע·מרתף with גג above it).
  const residentialFloors = Math.floor(above);
  // Round UP so a building with fewer units than floors still draws its units
  // (1 unit over 4 floors used to round to 0 per floor and draw nothing at all).
  const unitsPerFloor = perFloor !== null
    ? Math.max(0, Math.floor(perFloor))
    : (units !== null ? Math.max(1, Math.ceil(units / residentialFloors)) : null);
  const areaPerUnit = unitArea !== null
    ? unitArea
    : (typicalArea !== null && unitsPerFloor ? Math.round(typicalArea / unitsPerFloor) : null);

  // Top-down: roof, numbered floors, ground, basements.
  const floors = [{
    id: 'roof', level: null, name: 'גג', isRoof: true,
    elements: [{ type: 'roof', name: 'גג', area: roofArea, sourceField: 'roof_built_area_sqm' }],
  }];

  // Numbered bottom-up (דירה 1 on the first floor), rendered top-down.
  const numbered = [];
  let drawn = 0;
  for (let f = 1; f <= residentialFloors; f++) {
    const els = [];
    for (let u = 0; u < (unitsPerFloor || 0); u++) {
      // Never draw more apartments than the selected unit count — an evenly
      // spread estimate must not invent a unit the sources don't support.
      if (units !== null && drawn >= units) break;
      drawn += 1;
      els.push({
        type: 'apartment', name: `דירה ${drawn}`, area: areaPerUnit, floor: f,
        sourceField: unitArea !== null ? 'unit_area_avg_sqm' : 'typical_floor_area_sqm',
        // The per-unit area is a division of the floor area when no route
        // measured units directly — say so rather than present it as measured.
        derived: unitArea === null,
      });
    }
    numbered.push({
      id: `f${f}`,
      level: f,
      name: f === 1 ? 'ראשונה' : `קומה ${f}`,
      elements: els,
      units: els.filter((e) => e.type === 'apartment').length || null,
      area: typicalArea,
    });
  }
  // Balconies are spread across the floors that actually have units, and only
  // after those are known — dividing by the total floor count while rendering
  // on a subset silently dropped part of the area from the drawing.
  const withUnits = numbered.filter((fl) => fl.elements.some((e) => e.type === 'apartment'));
  if (balconyArea !== null && withUnits.length) {
    const share = Math.round(balconyArea / withUnits.length);
    for (const fl of withUnits) {
      fl.elements.push({
        type: 'balcony', name: 'מרפסות', area: share, floor: fl.level,
        sourceField: 'balcony_area_sqm', derived: true,
      });
    }
  }

  for (let i = numbered.length - 1; i >= 0; i--) floors.push(numbered[i]);

  floors.push({
    id: 'ground',
    level: 0,
    name: 'קרקע',
    elements: [{
      type: /מסחר/.test(String(groundType || '')) ? 'commercial' : (/עמודים|מפולש/.test(String(groundType || '')) ? 'pilotis' : 'apartment'),
      name: groundType || 'קומת קרקע',
      area: groundArea,
      sourceField: 'ground_floor_area_sqm',
    }],
    area: groundArea,
  });

  for (let i = 1; i <= (below || 0); i++) {
    floors.push({
      id: `b${i}`,
      level: -i,
      name: i === 1 ? 'מרתף' : `מרתף ${i}`,
      elements: [{ type: 'parking', name: 'חניה / מקלט', area: parkingArea, sourceField: 'ground_parking_area_sqm' }],
    });
  }

  const totals = {
    units: units,
    floors: above,
    area: num(val(fields, 'total_built_area_sqm')),
    mainArea: num(val(fields, 'main_area_sqm')),
  };
  return { floors, totals, ready: true };
}

// ─── Facades (§10 — the visual's column headers) ────────────────────────────
// A fixed set rather than free text: the matrix needs stable columns, and a
// unit's facade is a choice from four sides, not a sentence.
export const FACADES = [
  { id: 'front', name: 'קדמית' },
  { id: 'rear', name: 'אחורית' },
  { id: 'side_a', name: 'צד א׳' },
  { id: 'side_b', name: 'צד ב׳' },
];
export const facadeName = (id) => FACADES.find((f) => f.id === id)?.name || null;

// Only elements that actually face a direction can be placed on a facade. A
// roof, a parking level or a whole-floor commercial band spans the building and
// belongs across the row.
const FACING_TYPES = new Set(['apartment', 'balcony', 'closed_balcony']);
export const isFacadePlaceable = (el) => FACING_TYPES.has(el?.type) && (el?.level ?? 0) > 0;

export const ELEMENT_TYPES = {
  apartment: { label: 'דירות', tone: 'apt' },
  balcony: { label: 'מרפסות', tone: 'balcony' },
  closed_balcony: { label: 'מרפסות סגורות', tone: 'closed' },
  commercial: { label: 'מסחר', tone: 'commercial' },
  pilotis: { label: 'עמודים מפולשת', tone: 'pilotis' },
  parking: { label: 'חניה / מקלט', tone: 'parking' },
  roof: { label: 'גג', tone: 'roof' },
};

// How much of what a route reported on it actually answered. The routes list
// used to show a "חוסרים" count, which collided with the consolidation's own
// "חסר" — that one counts fields no route could answer, this one counts fields
// a single route had nothing to say about. Two different ideas, one word.
export function routeAnswerCounts(route) {
  const obs = route?.run?.observations || [];
  return { answered: obs.filter((o) => o.value !== null).length, total: obs.length };
}

// Progress against the §13 gate, for the goal strip at the top of the screen.
export function requiredProgress(fields) {
  const req = (fields || []).filter((f) => f.required);
  return {
    answered: req.filter((f) => fieldValueIsUsable(f)).length,
    total: req.length,
    missing: req.filter((f) => !fieldValueIsUsable(f)),
  };
}

// Which routes could answer a field, best candidate first: one that already ran
// and has a value, then one that is ready to run, then anything else. Powers the
// "go fill this" jump from a required-but-missing row back into the routes list.
export function routesForField(fieldId, routes) {
  const def = fieldDef(fieldId);
  const ids = def?.routes || [];
  const rank = (r) => {
    if (!r) return 9;
    if ((r.run?.observations || []).some((o) => o.fieldId === fieldId && o.value !== null)) return 0;
    if (r.status === 'ready') return 1;
    if (r.id === 'manual_input') return 2;
    if (r.status === 'missing_docs') return 3;
    return 4;
  };
  return ids
    .map((id) => (routes || []).find((r) => r.id === id))
    .filter(Boolean)
    .sort((a, b) => rank(a) - rank(b));
}

// A field's selected value must be usable by the downstream calculation, not
// merely present: "1,200" typed into a numeric field used to satisfy the gate
// while every consumer read it as text.
export function fieldValueIsUsable(field) {
  if (!field || field.selectedValue === null || field.selectedValue === undefined) return false;
  const def = fieldDef(field.fieldId);
  if (def?.type === 'number') return Number.isFinite(Number(field.selectedValue));
  return String(field.selectedValue).trim() !== '';
}

// ─── Completion gate (§13) ──────────────────────────────────────────────────
export function completionState(vm) {
  const routes = vm?.routes || [];
  const fields = vm?.fields || [];
  const ranRoutes = routes.filter((r) => r.run && r.run.observations?.length);
  const required = fields.filter((f) => f.required);
  const missingRequired = required.filter((f) => !fieldValueIsUsable(f));
  // The FLAG, not the status: `approve` overwrites `status` without
  // re-consolidating, so reading the status let one click on «אשר» clear a live
  // contradiction and open the gate — and it persisted through a reload.
  const unresolvedConflicts = fields.filter((f) => f.conflict && !f.userResolution);
  const schematic = buildSchematic(fields);
  // `ready` only means the schematic can RENDER, which needs nothing but a floor
  // count. The §13 condition is that a schematic was actually built, so require
  // it to carry substance too — a stack of empty floor bands is not a building.
  const schematicBuilt = schematic.ready
    && schematic.totals?.units != null
    && schematic.totals?.area != null;
  return {
    hasRoutes: routes.length === ROUTES.length,
    atLeastOneRun: ranRoutes.length > 0,
    missingRequired,
    unresolvedConflicts,
    schematicReady: schematicBuilt,
    ok: ranRoutes.length > 0 && !missingRequired.length && !unresolvedConflicts.length && schematicBuilt,
  };
}

// ─── Build ──────────────────────────────────────────────────────────────────
const logTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
};
let logSeq = 0;
export const exLogEvent = (text, kind = 'info') => ({ id: `evt-ex-${Date.now()}-${logSeq++}`, time: logTime(), text, kind });

// Assemble the stage. `ctx` is { identification, statutory }. Auto routes are
// marked ready/missing-docs but NOT run — the user presses "הפעלה" per §5.
//
// `prior` is a previous snapshot for the SAME property whose structure is out of
// date (a version bump). Its route documents and per-field decisions are carried
// across: rebuilding from scratch used to throw away every attached document,
// manual entry, override, approval and note, and then persist over the backup.
export function buildExisting(ctx, signature, prior = null) {
  const log = [exLogEvent('מכין את מסלולי ההסקה למצב הקיים', 'done')];
  const priorDocs = new Map((prior?.routes || []).map((r) => [r.id, r.documents || []]));
  const carriedDocs = [...priorDocs.values()].reduce((n, d) => n + d.length, 0);
  // Only decisions consolidateField actually preserves count as carried.
  const priorFields = (prior?.fields || []).filter((f) => ['override', 'user_input'].includes(f.status) || f.note);
  const carriedFacades = (prior?.elements || []).filter((e) => e.orientation).length;
  if (carriedDocs || priorFields.length || carriedFacades) {
    log.push(exLogEvent(
      `נשמרו מהגרסה הקודמת: ${carriedDocs} מסמכים, ${priorFields.length} החלטות על נתונים ו-${carriedFacades} שיוכי חזית`,
      'done'
    ));
  }
  if (reviewedField(ctx?.statutory, 'units_existing') || reviewedField(ctx?.statutory, 'coverage_actual')) {
    log.push(exLogEvent('נטענו נתוני מצב קיים שאושרו בשלב הסטטוטורי', 'done'));
  }
  const routes = ROUTES.map((r) => {
    const docs = priorDocs.get(r.id) || [];
    const status = routeState(r, docs, ctx);
    return { ...r, documents: docs, status, run: null };
  });
  const ready = routes.filter((r) => r.status === 'ready').length;
  log.push(exLogEvent(
    ready ? `${ready} מסלולים מוכנים להפעלה מתוך ${routes.length}` : 'אין מסלולים מוכנים — נדרשת השלמת מסמכים',
    ready ? 'done' : 'warn'
  ));
  return {
    builtVersion: EX_VERSION,
    signature,
    routes,
    fields: consolidate(routes, prior?.fields || []),
    // Facade assignments are the one datum no route can produce, so losing them
    // to a version bump loses the only knowledge that is purely the user's.
    elements: prior?.elements || [],
    log,
  };
}
